<script setup>
import MagnifyingGlassMinusIcon from '@iconify-vue/ph/magnifying-glass-minus';
import MagnifyingGlassPlusIcon from '@iconify-vue/ph/magnifying-glass-plus';
import XIcon from '@iconify-vue/ph/x';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import { AnimatePresence, Motion } from 'motion-v';
import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    VisuallyHidden,
} from 'reka-ui';
import { ref, watch } from 'vue';

const {
    src,
    href,
    alt = undefined,
    align = 'left',
    size = 'medium',
    withoutBorder = false,
    disableZoom = false,
} = defineProps({
    src: {
        type: String,
        required: true,
    },
    href: {
        type: String,
        required: false,
    },
    alt: {
        type: String,
        required: false,
    },
    align: {
        type: String,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    withoutBorder: {
        type: Boolean,
        required: false,
    },
    disableZoom: {
        type: Boolean,
        required: false,
    },
});

const lightboxOpen = ref(false);

const scale = ref(1);

const translateX = ref(0);

const translateY = ref(0);

const isDragging = ref(false);

const dragStart = ref({ x: 0, y: 0 });

function resetZoom() {
    scale.value = 1;
    translateX.value = 0;
    translateY.value = 0;
}

function zoomIn() {
    scale.value = Math.min(5, scale.value + 0.5);
    if (scale.value === 1) {
        translateX.value = 0;
        translateY.value = 0;
    }
}

function zoomOut() {
    scale.value = Math.max(1, scale.value - 0.5);
    if (scale.value === 1) {
        translateX.value = 0;
        translateY.value = 0;
    }
}

function onWheel(e) {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
}

function onPointerDown(e) {
    if (scale.value <= 1) return;
    isDragging.value = true;
    dragStart.value = {
        x: e.clientX - translateX.value,
        y: e.clientY - translateY.value,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
}

function onPointerMove(e) {
    if (!isDragging.value) return;
    translateX.value = e.clientX - dragStart.value.x;
    translateY.value = e.clientY - dragStart.value.y;
}

function onPointerUp() {
    isDragging.value = false;
}

watch(lightboxOpen, (open) => {
    if (!open) resetZoom();
});
</script>

<template>
    <BaseCell :align="align">
        <component
            :is="href ? 'a' : 'div'"
            :href="href ? href : undefined"
            :target="href ? '_blank' : undefined"
            class="block rounded bg-white"
            :class="{
                'cursor-zoom-in': !href && !disableZoom,
                'border border-gray-300': !withoutBorder,
            }"
            @click="!href && !disableZoom ? (lightboxOpen = true) : undefined"
        >
            <img
                :src="src"
                :alt="alt"
                class="object-contain"
                :class="{
                    'size-15 max-w-15': size === 'small',
                    'size-30 max-w-30': size === 'medium',
                    'size-45 max-w-45': size === 'large',
                }"
            />
        </component>

        <DialogRoot v-if="!href && !disableZoom" v-model:open="lightboxOpen">
            <DialogPortal forceMount>
                <AnimatePresence>
                    <Motion
                        v-if="lightboxOpen"
                        key="overlay"
                        :initial="{ opacity: 0 }"
                        :animate="{ opacity: 1 }"
                        :exit="{ opacity: 0 }"
                        :transition="{ duration: 0.2 }"
                        class="fixed inset-0 z-50"
                    >
                        <DialogOverlay
                            forceMount
                            class="size-full bg-black/80"
                        />
                    </Motion>

                    <Motion
                        v-if="lightboxOpen"
                        key="content"
                        :initial="{ opacity: 0, scale: 0.96 }"
                        :animate="{ opacity: 1, scale: 1 }"
                        :exit="{ opacity: 0, scale: 0.96 }"
                        :transition="{
                            type: 'spring',
                            duration: 0.3,
                            bounce: 0.1,
                        }"
                        class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center"
                    >
                        <DialogContent
                            forceMount
                            class="pointer-events-auto relative max-h-[90vh] max-w-[90vw] focus:outline-none"
                            :aria-describedby="undefined"
                        >
                            <VisuallyHidden>
                                <DialogTitle />
                            </VisuallyHidden>

                            <div
                                class="absolute -top-12 right-0 left-0 flex items-center justify-between"
                            >
                                <div class="flex gap-2">
                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="scale <= 1"
                                        title="Dézoomer"
                                        @click="zoomOut"
                                    >
                                        <MagnifyingGlassMinusIcon
                                            class="size-5"
                                        />
                                    </button>

                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="scale >= 5"
                                        title="Zoomer"
                                        @click="zoomIn"
                                    >
                                        <MagnifyingGlassPlusIcon
                                            class="size-5"
                                        />
                                    </button>
                                </div>

                                <DialogClose
                                    class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 hover:bg-indigo-700"
                                >
                                    <XIcon class="size-5" />
                                </DialogClose>
                            </div>

                            <div
                                class="overflow-hidden rounded-lg shadow-lg"
                                @wheel="onWheel"
                                @dblclick="resetZoom"
                            >
                                <img
                                    :src="src"
                                    :alt="alt"
                                    :style="{
                                        transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
                                    }"
                                    class="max-h-[85vh] max-w-[85vw] object-contain transition-transform duration-150"
                                    :class="{
                                        'cursor-grab': scale > 1 && !isDragging,
                                        'cursor-grabbing': isDragging,
                                        '!duration-0': isDragging,
                                    }"
                                    draggable="false"
                                    @pointerdown="onPointerDown"
                                    @pointermove="onPointerMove"
                                    @pointerup="onPointerUp"
                                />
                            </div>
                        </DialogContent>
                    </Motion>
                </AnimatePresence>
            </DialogPortal>
        </DialogRoot>
    </BaseCell>
</template>
